(() => {
  'use strict';
  const config = document.getElementById('pfh-algolia-config');
  if (!config || window.pfhAlgoliaSearch) return;
  const ALG = JSON.parse(config.textContent);
  const enabled = Boolean(ALG.app && ALG.key && ALG.index);
  const money = cents => '$' + (cents / 100).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const esc = value => String(value ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const cents = value => Math.round(Number(value || 0) * 100);
  const available = hit => hit.inventory_available === true || hit.inventory_available === 'true' || Number(hit.inventory_available) > 0;
  const colourOf = hit => {
    const names = Array.isArray(hit.option_names) ? hit.option_names : [];
    const index = names.findIndex(name => /^(colour|color)$/i.test(name));
    return index < 0 ? null : hit['option' + (index + 1)];
  };
  function rating(hit) {
    const reviews = hit.meta?.reviews || {};
    function value(raw) {
      for (let i = 0; i < 4; i++) {
        if (raw && typeof raw === 'object') raw = raw.value;
        else if (typeof raw === 'string') {
          try { raw = JSON.parse(raw); } catch (_) { return Number(raw); }
        } else break;
      }
      return Number(raw);
    }
    const avg = value(reviews.rating), count = value(reviews.rating_count);
    return Number.isFinite(avg) && avg > 0 && avg <= 5 && Number.isFinite(count) && count > 0
      ? { avg, count: Math.floor(count) } : null;
  }
  function safeImage(raw) {
    try {
      const url = new URL(typeof raw === 'object' ? raw?.src : raw, location.origin);
      if (!raw || !['https:', 'http:'].includes(url.protocol)) return '';
      url.searchParams.set('width', '400');
      return url.href;
    } catch (_) { return ''; }
  }
  async function request(url, options = {}, signal) {
    const controller = new AbortController();
    const abort = () => controller.abort();
    if (signal?.aborted) abort();
    signal?.addEventListener('abort', abort, { once: true });
    const timer = setTimeout(abort, 2000);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      if (!response.ok) throw new Error('Search unavailable');
      return await response.json();
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener('abort', abort);
    }
  }
  function algoliaQuery(q, page, signal, distinct, perPage, wholeWords) {
    return request('https://' + ALG.app + '-dsn.algolia.net/1/indexes/' + encodeURIComponent(ALG.index) + '/query', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Algolia-Application-Id': ALG.app, 'X-Algolia-API-Key': ALG.key },
      body: JSON.stringify({ query: q, page, hitsPerPage: perPage || 1000, distinct: distinct !== false, typoTolerance: 'min', clickAnalytics: true,
        /* The results page tries whole words first ("blue" is not "bluetooth") and falls back to
           partial words when that finds little ("harnes"). The dropdown always matches as you type. */
        queryType: wholeWords ? 'prefixNone' : 'prefixLast',
        restrictSearchableAttributes: ['title', 'variant_title', 'product_type', 'tags', 'handle', 'sku', 'body_html_safe'],
        attributesToHighlight: [], attributesToSnippet: [],
        attributesToRetrieve: ['handle', 'title', 'product_image', 'image', 'price', 'compare_at_price', 'product_type', 'inventory_available', 'option_names', 'option1', 'option2', 'option3', 'meta.reviews'] })
    }, signal);
  }
  /* Words in the search that could be a colour ("pink", "light blue"). When a product has a
     variant in that colour, its card shows that variant's photo and opens on that variant,
     so a search for "pink" looks pink instead of showing each product's main photo. */
  const queryWords = q => String(q || '').toLowerCase().split(/[^a-z]+/).filter(w => w.length >= 3);
  const colourMatches = (colour, words) => {
    if (!colour || !words.length) return false;
    const parts = String(colour).toLowerCase().split(/[^a-z]+/);
    return words.some(w => parts.includes(w));
  };
  function dedupe(hits, q) {
    const words = queryWords(q);
    const seen = new Map();
    hits.forEach(hit => {
      if (!hit.handle || hit.product_type === 'PPLR_HIDDEN_PRODUCT') return;
      const colour = colourOf(hit);
      let entry = seen.get(hit.handle);
      if (!entry) {
        entry = { hit, colours: new Set(), prices: [] };
        seen.set(hit.handle, entry);
      }
      if (colour) entry.colours.add(colour);
      if (hit.price != null && Number.isFinite(Number(hit.price))) {
        entry.prices.push(cents(hit.price));
      }
      if (!available(entry.hit) && available(hit)) entry.hit = hit;
      if (colourMatches(colour, words) && (!entry.match || (!available(entry.match) && available(hit)))) entry.match = hit;
    });
    return Array.from(seen, ([handle, entry]) => {
      const match = entry.match;
      const hit = match || entry.hit;
      const prices = entry.prices;
      const variantId = match && String(match.objectID || '').replace(/\D/g, '');
      return {
        handle, url: ALG.productsUrl + encodeURIComponent(handle) + (variantId ? '?variant=' + variantId : ''), title: hit.title || '',
        image: safeImage(match ? (match.image || match.product_image) : (hit.product_image || hit.image)), price: prices.length ? Math.min(...prices) : 0,
        varies: new Set(entry.prices).size > 1, colours: entry.colours.size, rating: rating(hit),
        type: hit.product_type || 'Other', available: available(hit),
        objectID: hit.objectID, position: hit._pfhPosition, queryID: hit._pfhQueryID
      };
    });
  }
  async function algoliaSearch(q, limit) {
    /* Records are one per variant and the Algolia Shopify app manages index grouping, so we
       group variants into products here. One request: the dropdown asks for a small slice,
       the results page for Algolia's maximum of 1,000 variants (enough for any real query).
       When more variants matched than were returned, the count is marked as a minimum. */
    const controller = new AbortController();
    const deadline = setTimeout(() => controller.abort(), 2000);
    try {
      let data = await algoliaQuery(q, 0, controller.signal, false, limit ? limit * 8 : 1000, !limit);
      if (!Array.isArray(data.hits)) throw new Error('Invalid search response');
      if (!limit && new Set(data.hits.map(h => h.handle)).size < 5) {
        const loose = await algoliaQuery(q, 0, controller.signal, false, 1000, false);
        if (Array.isArray(loose.hits) && loose.hits.length > data.hits.length) data = loose;
      }
      const hits = data.hits.map((hit, index) => ({ ...hit, _pfhPosition: index + 1, _pfhQueryID: data.queryID }));
      const products = dedupe(hits, q).sort((x, y) => x.position - y.position);
      const capped = Number(data.nbHits) > hits.length;
      return { products, total: products.length, exact: !capped, capped };
    } finally { clearTimeout(deadline); }
  }
  async function nativeSearch(q) {
    const data = await request(ALG.searchUrl + '/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=6');
    const products = data.resources?.results?.products;
    if (!Array.isArray(products)) throw new Error('Invalid predictive response');
    return { exact: false, products: products.slice(0, 6).map(p => ({
      handle: p.handle, url: ALG.productsUrl + encodeURIComponent(p.handle), title: p.title,
      image: safeImage(p.image || p.featured_image), price: cents(p.price_min ?? p.price),
      varies: p.price_max != null && Number(p.price_max) !== Number(p.price_min ?? p.price),
      rating: rating(p), colours: 0
    })) };
  }
  async function search(q, limit) {
    if (enabled) {
      try { return await algoliaSearch(q, limit); } catch (_) { /* Shopify remains available. */ }
    }
    return nativeSearch(q);
  }
  function card(product, compact = false) {
    const r = product.rating;
    const stars = r ? '<span class="pfh-search-rating" aria-label="' + r.avg.toFixed(1) + ' out of 5, ' + r.count + ' reviews"><span aria-hidden="true">' + '★'.repeat(Math.round(r.avg)) + '☆'.repeat(5 - Math.round(r.avg)) + '</span> ' + r.avg.toFixed(1) + ' (' + r.count + ')</span>' : '';
    const tracking = product.queryID && product.objectID ? ' data-pfh-hit="' + esc(JSON.stringify({ handle: product.handle, objectID: product.objectID, queryID: product.queryID, position: product.position })) + '"' : '';
    return '<a class="pfh-search-card ' + (compact ? 'pfh-search-card--compact' : 'search-item shop-list-wrap') + '" href="' + esc(product.url) + '"' + tracking + '>' +
      '<span class="search-item-image">' + (product.image ? '<img src="' + esc(product.image) + '" alt="" loading="lazy" width="400" height="400">' : '') + '</span>' +
      '<span class="search-item-content"><span class="pfh-search-card__title">' + esc(product.title) + '</span>' +
      '<span class="search-price">' + (product.varies ? 'From ' : '') + money(product.price) + '</span>' +
      stars + (product.colours > 1 ? '<span class="pfh-search-card__colours">' + product.colours + ' colours</span>' : '') + '</span></a>';
  }

  /* Same seven-day, handle-based attribution as the reference implementation. */
  function qmapRead() {
    try {
      return Object.fromEntries(Object.entries(JSON.parse(localStorage.getItem('pfh_alg_q') || '{}'))
        .filter(([, record]) => record && Date.now() - record.t < 7 * 86400000));
    } catch (_) { return {}; }
  }
  function qmapWrite(handle, objectID, queryID) {
    try {
      const map = qmapRead();
      map[handle] = { o: String(objectID), q: String(queryID), t: Date.now() };
      localStorage.setItem('pfh_alg_q', JSON.stringify(Object.fromEntries(Object.entries(map).sort((a, b) => b[1].t - a[1].t).slice(0, 20))));
    } catch (_) { /* Storage can be disabled. */ }
  }
  function insights(event) {
    if (!enabled || window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === false) return;
    try {
      let token = localStorage.getItem('pfh_search_user');
      if (!token) {
        token = 'u-' + Math.random().toString(36).slice(2) + '-' + Date.now().toString(36);
        localStorage.setItem('pfh_search_user', token);
      }
      localStorage.setItem('pfh_alg_cfg', JSON.stringify({ app: ALG.app, key: ALG.key, index: ALG.index }));
      fetch('https://insights.algolia.io/1/events', {
        method: 'POST', keepalive: true,
        headers: { 'Content-Type': 'application/json', 'X-Algolia-Application-Id': ALG.app, 'X-Algolia-API-Key': ALG.key },
        body: JSON.stringify({ events: [{ ...event, index: ALG.index, userToken: token }] })
      }).catch(() => {});
    } catch (_) { /* Analytics must never interrupt shopping. */ }
  }
  document.addEventListener('click', event => {
    const link = event.target.closest('[data-pfh-hit]');
    if (!link) return;
    const hit = JSON.parse(link.dataset.pfhHit);
    qmapWrite(hit.handle, hit.objectID, hit.queryID);
    insights({ eventType: 'click', eventName: 'Product Clicked After Search', queryID: hit.queryID,
      objectIDs: [String(hit.objectID)], positions: [hit.position] });
  });
  if (ALG.product) {
    let lastAdd = 0;
    function trackAdd(form) {
      if (!form) return;
      const id = form.querySelector('[name="id"]')?.value;
      const record = qmapRead()[ALG.product.handle];
      if (!record || !Object.hasOwn(ALG.product.prices, id) || Date.now() - lastAdd < 1500) return;
      if (!form.checkValidity()) return;
      lastAdd = Date.now();
      const quantity = Math.max(1, Number(form.querySelector('[name="quantity"]')?.value) || 1);
      insights({ eventType: 'conversion', eventSubtype: 'addToCart', eventName: 'Product Added To Cart After Search',
        queryID: record.q, objectIDs: [record.o], currency: ALG.currency,
        objectData: [{ quantity, price: ALG.product.prices[id] / 100 }] });
      qmapWrite(ALG.product.handle, record.o, record.q);
    }
    document.addEventListener('submit', event => {
      if (event.target.matches('form[action*="/cart/add"]')) trackAdd(event.target);
    }, true);
    document.addEventListener('click', event => {
      const button = event.target.closest('[name="add"], [data-add-to-cart], .enj-add-to-cart-btn, .product-form__submit, .btn_add_to_cart, .add_to_cart');
      if (button && !button.disabled) trackAdd(button.closest('form[action*="/cart/add"]'));
    }, true);
  }

  let nextId = 0;
  function attachInputs() {
    document.querySelectorAll('[data-pfh-empty-search] .search-input').forEach(input => {
      if (input.dataset.pfhAlgolia) return;
      input.dataset.pfhAlgolia = 'true';
      const root = input.closest('[data-pfh-empty-search]'), form = input.form;
      const empty = root.querySelector('.pfh-search-empty');
      const panel = document.createElement('div');
      panel.className = 'pfh-search-dropdown';
      panel.id = 'pfh-search-dropdown-' + (++nextId);
      panel.hidden = true;
      panel.setAttribute('role', 'region');
      panel.setAttribute('aria-label', 'Product search results');
      root.append(panel);
      let timer, version = 0;
      function close() {
        clearTimeout(timer);
        version++;
        panel.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        if (input.getAttribute('aria-controls') === panel.id) input.removeAttribute('aria-controls');
      }
      function resize() {
        const viewport = window.visualViewport;
        const bottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
        panel.style.setProperty('--pfh-search-space', Math.max(0, bottom - panel.getBoundingClientRect().top - 12) + 'px');
      }
      function open() {
        if (empty) empty.hidden = true;
        panel.hidden = false;
        input.setAttribute('aria-controls', panel.id);
        input.setAttribute('aria-expanded', 'true');
        resize();
      }
      function run() {
        close();
        const q = input.value.trim();
        if (q.length < 2 || input.composing) return;
        const current = version;
        panel.innerHTML = '<p role="status">Searching products…</p>';
        open();
        timer = setTimeout(async () => {
          try {
            const result = await search(q, 6);
            if (version !== current) return;
            panel.innerHTML = '<div class="pfh-search-dropdown__products">' + result.products.slice(0, 6).map(p => card(p, true)).join('') + '</div>' +
              (!result.products.length ? '<p role="status">No matching products. Try another search.</p>' : '') +
              '<button type="button" class="pfh-search-button">' + (result.exact ? 'View all ' + result.total + ' results' : 'View all results') + '</button>';
            panel.querySelector('button').addEventListener('click', () => form.requestSubmit());
          } catch (_) {
            if (version !== current) return;
            panel.innerHTML = '<p role="status">Press Search to see results.</p><button type="button" class="pfh-search-button">Search</button>';
            panel.querySelector('button').addEventListener('click', () => form.requestSubmit());
          }
        }, 150);
      }
      input.addEventListener('input', run);
      input.addEventListener('focus', run);
      input.addEventListener('compositionstart', () => { input.composing = true; close(); });
      input.addEventListener('compositionend', () => { input.composing = false; run(); });
      root.addEventListener('keydown', event => {
        if (event.key === 'Escape') { close(); input.focus(); close(); return; }
        if (panel.hidden || !['ArrowDown', 'ArrowUp'].includes(event.key)) return;
        const items = [input, ...panel.querySelectorAll('a, button')];
        const index = items.indexOf(document.activeElement);
        event.preventDefault();
        items[(index + (event.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length].focus();
      });
      root.addEventListener('focusout', () => setTimeout(() => { if (!root.contains(document.activeElement)) close(); }, 0));
      document.addEventListener('pointerdown', event => { if (!root.contains(event.target)) close(); });
      form.addEventListener('submit', close);
      window.addEventListener('resize', resize);
      window.visualViewport?.addEventListener('resize', resize);
      window.visualViewport?.addEventListener('scroll', resize);
      document.addEventListener('shopify:section:unload', close);
    });
  }

  function attachResults() {
    document.querySelectorAll('[data-pfh-results]').forEach(async root => {
      if (!enabled || root.dataset.pfhInitialised) return;
      root.dataset.pfhInitialised = 'true';
      const q = new URLSearchParams(location.search).get('q') || '';
      if (!q.trim()) return;
      const get = name => root.querySelector('[data-pfh-' + name + ']');
      const state = { all: [], types: [], price: null, sort: 'relevance', shown: 24 };
      const buckets = ['Under $25', '$25 to $50', '$50 to $100', '$100 to $200', '$200 and over'];
      const bucket = price => buckets[price < 2500 ? 0 : price < 5000 ? 1 : price < 10000 ? 2 : price < 20000 ? 3 : 4];
      function render() {
        const list = state.all.filter(p => (!state.types.length || state.types.includes(p.type)) && (!state.price || bucket(p.price) === state.price));
        if (state.sort === 'price_asc') list.sort((a, b) => a.price - b.price);
        if (state.sort === 'price_desc') list.sort((a, b) => b.price - a.price);
        if (state.sort === 'title_asc') list.sort((a, b) => a.title.localeCompare(b.title));
        get('count').textContent = list.length + (list.length === 1 ? ' product' : ' products') + ' for “' + q + '”';
        get('title').textContent = list.length + (list.length === 1 ? ' result' : ' results') + ' for “' + q + '”';
        get('grid').innerHTML = list.slice(0, state.shown).map(p => card(p)).join('');
        get('more').hidden = list.length <= state.shown;
        get('no-results').hidden = list.length > 0;
        const none = !state.all.length;
        root.querySelector('.pfh-results__body > aside').hidden = none;
        root.querySelector('.pfh-results__body').classList.toggle('pfh-results__body--empty', none);
        get('sort').closest('label').hidden = none;
        const chips = [...state.types.map(v => ['type', v]), ...(state.price ? [['price', state.price]] : [])];
        get('chips').innerHTML = chips.map(([key, value]) => '<button type="button" data-clear="' + key + '" data-value="' + esc(value) + '" aria-label="Remove ' + esc(value) + ' filter">' + esc(value) + ' <span aria-hidden="true">×</span></button>').join('');
        get('filters').querySelectorAll('input').forEach(input => {
          input.checked = input.dataset.facet === 'type' ? state.types.includes(input.value) : state.price === input.value;
        });
      }
      try {
        state.all = (await algoliaSearch(q)).products;
        if (!root.isConnected) return;
        const counts = { type: new Map(), price: new Map() };
        state.all.forEach(p => {
          counts.type.set(p.type, (counts.type.get(p.type) || 0) + 1);
          counts.price.set(bucket(p.price), (counts.price.get(bucket(p.price)) || 0) + 1);
        });
        get('filters').innerHTML = [['Product type', 'type', [...counts.type.keys()].sort()], ['Price', 'price', buckets.filter(b => counts.price.has(b))]].map(([title, key, values]) =>
          '<fieldset><legend>' + title + '</legend>' + values.map(value => '<label><input type="' + (key === 'price' ? 'radio' : 'checkbox') + '" name="pfh-' + key + '" data-facet="' + key + '" value="' + esc(value) + '"><span>' + esc(value) + '</span><span>' + counts[key].get(value) + '</span></label>').join('') + '</fieldset>').join('');
        get('filters').addEventListener('change', event => {
          const input = event.target;
          if (!input.matches('[data-facet]')) return;
          if (input.dataset.facet === 'price') state.price = input.checked ? input.value : null;
          else state.types = [...get('filters').querySelectorAll('[data-facet="type"]:checked')].map(i => i.value);
          state.shown = 24; render();
        });
        get('chips').addEventListener('click', event => {
          const button = event.target.closest('[data-clear]');
          if (!button) return;
          if (button.dataset.clear === 'price') state.price = null;
          else state.types = state.types.filter(v => v !== button.dataset.value);
          state.shown = 24; render();
        });
        get('sort').addEventListener('change', event => { state.sort = event.target.value; state.shown = 24; render(); });
        get('more').addEventListener('click', () => {
          const firstNew = state.shown;
          state.shown += 24; render();
          get('grid').children[firstNew]?.focus();
        });
        get('filter-toggle').addEventListener('click', event => {
          const open = event.currentTarget.getAttribute('aria-expanded') !== 'true';
          event.currentTarget.setAttribute('aria-expanded', String(open));
          get('filters').classList.toggle('is-open', open);
        });
        get('title').textContent = 'Results for “' + q + '”';
        document.querySelectorAll('.breadcrumb li, .breadcrumb-content li, [class*="breadcrumb"] li, [class*="breadcrumb"] span').forEach(el => { if (!el.children.length && /results found for/i.test(el.textContent)) el.textContent = 'Search'; });
        render();
        get('enhanced').hidden = false;
        get('server-results').hidden = true;
      } catch (_) { /* Preserve complete, paginated server-side Shopify results. */ }
    });
  }
  window.pfhAlgoliaSearch = { search, algoliaSearch, dedupe, rating, card };
  attachInputs();
  attachResults();
  document.addEventListener('shopify:section:load', () => { attachInputs(); attachResults(); });
})();
